import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import React from "react";

import CollectionStyleItem from "@is/components/CollectionStyleItem";
import { CollectionStyleItemVariant } from "@is/constants";
import { Collection, CollectionStyle } from "@is/types";

import css from "./CollectionList.module.css";
import useManageCollectionList from "./hooks/useManageCollectionList";

type Props = {
  collection: Collection;
  onRemove: (index: number) => void;
  onReorder: (items: CollectionStyle[]) => void;
  /**
   * Renders the items for browsing only — no drag-to-reorder and no remove. Used for
   * brand-wide collections, which are managed from the admin dashboard.
   */
  readOnly?: boolean;
};

const CollectionList = ({
  collection,
  onRemove,
  onReorder,
  readOnly = false,
}: Props) => {
  const { t, collectionItems, containerHeight, handleDragEnd } =
    useManageCollectionList(collection, onReorder);

  const items = collectionItems.map((favorite, index) => (
    <CollectionStyleItem
      key={`${favorite.styleId}-${favorite.variantId}`}
      collectionStyle={favorite}
      variant={CollectionStyleItemVariant.IN_COLLECTION}
      index={index}
      onRemove={readOnly ? undefined : onRemove}
      readOnly={readOnly}
    />
  ));

  return (
    <>
      {collectionItems.length === 0 && (
        <div className={css.subtitle}>
          {t("You have not added any items to your collection.")}
        </div>
      )}

      {readOnly ? (
        <div className={css.collection} style={{ minHeight: containerHeight }}>
          {items}
        </div>
      ) : (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <div
            className={css.collection}
            style={{ minHeight: containerHeight }}
          >
            <SortableContext
              items={collectionItems.map((item) => item.variantId)}
              strategy={verticalListSortingStrategy}
            >
              {items}
            </SortableContext>
          </div>
        </DndContext>
      )}
    </>
  );
};

export default CollectionList;
